import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/App";
import PromoBanner from "@/components/site/PromoBanner";

const NAV = [
  { href: "#services", label: "Services" },
  { href: "#rates", label: "Rates" },
  { href: "#calculator", label: "Calculator" },
  { href: "#gallery", label: "Gallery" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar({ onGetQuote }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-slate-200/70" : "bg-transparent"
      }`}
    >
      <PromoBanner onCtaClick={onGetQuote} />
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-xl bg-orange-500 grid place-items-center text-white font-display font-black">
            B
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-ink text-[15px]">
              {BUSINESS.name}
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-muted-ink">
              Since 2015 · Bangalore
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-sm font-semibold text-ink-2 hover:text-orange-600 transition-colors"
              data-testid={`nav-link-${n.label.toLowerCase()}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-ink hover:text-orange-600 transition-colors"
            data-testid="nav-call"
          >
            <Phone className="h-4 w-4" />
            {BUSINESS.phone}
          </a>
          <Button
            onClick={onGetQuote}
            data-testid="nav-cta-quote"
            className="hidden md:inline-flex bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-5 shadow-brand"
          >
            Get Free Quote
          </Button>
          <button
            className="lg:hidden p-2 rounded-lg hairline bg-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200/70 bg-white" data-testid="nav-mobile-panel">
          <div className="px-5 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-2 text-base font-semibold text-ink"
              >
                {n.label}
              </a>
            ))}
            <Button
              onClick={() => {
                setOpen(false);
                onGetQuote();
              }}
              className="mt-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full"
              data-testid="nav-mobile-cta"
            >
              Get Free Quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
